import type { ConfigSide, StageConfig, StageControlConfig, StageControlLevel } from '@/api/config';

const CONTROL_LEVEL_LABELS: Record<StageControlLevel, string> = {
  force: '强制',
  strict: '严格',
  normal: '普通',
  suggested: '建议',
  flexible: '灵活',
};

const CONTROL_LEVEL_BY_LABEL: Record<string, StageControlLevel> = {
  强制: 'force',
  force: 'force',
  严格: 'strict',
  strict: 'strict',
  普通: 'normal',
  normal: 'normal',
  建议: 'suggested',
  suggested: 'suggested',
  灵活: 'flexible',
  flexible: 'flexible',
};

export interface ImportedPromptText {
  text: string;
  name?: string;
}

export interface ImportedStageText {
  stageControl: StageControlConfig;
  name?: string;
}

const normalizeNewlines = (value: string): string => value.replace(/^\uFEFF/, '').replace(/\r\n?/g, '\n');

const textBlock = (label: string, value: string): string => `${label}：\n<<<\n${value.trim()}\n>>>`;

const yesNo = (value: boolean): string => (value ? '是' : '否');

const parseYesNo = (value: string | undefined, fallback = false): boolean => {
  if (!value) return fallback;
  return /^(是|true|1|开启|启用|on)$/i.test(value.trim());
};

const readScalar = (block: string, label: string): string | undefined => {
  const match = block.match(new RegExp(`^${label}[：:]\\s*(.+?)\\s*$`, 'm'));
  return match?.[1]?.trim();
};

const readTextBlock = (block: string, label: string): string => {
  const match = block.match(new RegExp(`${label}[：:]\\s*\\n<<<\\n([\\s\\S]*?)\\n>>>`));
  return match?.[1]?.trim() || '';
};

const readList = (block: string, label: string): string[] => {
  const match = block.match(new RegExp(`${label}[：:]\\s*\\n((?:- .*?(?:\\n|$))*)`));
  if (!match?.[1]) return [];
  return match[1]
    .split('\n')
    .map((line) => line.replace(/^-\s*/, '').trim())
    .filter(Boolean);
};

const parseKeywords = (value: string | undefined): string[] =>
  (value || '')
    .split(/[|｜，,\n]/)
    .map((item) => item.trim())
    .filter(Boolean);

const stageBlock = (stage: StageConfig, heading: string): string => {
  const conditions = stage.conditions || {};
  const examples = (stage.example_questions || []).map((item) => `- ${item.trim()}`).filter((item) => item !== '- ').join('\n');
  const maxTurns = conditions.max_turns === '' || conditions.max_turns === undefined ? '' : String(conditions.max_turns);

  return [
    heading,
    `标识：${stage.id}`,
    `名称：${stage.name}`,
    `开启：${yesNo(stage.enabled)}`,
    `条件关系：${conditions.relation === 'and' ? '且' : '或'}`,
    `最大会话轮次：${maxTurns}`,
    `超过最大轮次强制推进：${yesNo(Boolean(conditions.force_advance_on_max_turns))}`,
    `关键词：${(conditions.keywords || []).join(' | ')}`,
    textBlock('侧重点', stage.focus || ''),
    textBlock('阶段提示词', stage.prompt || ''),
    textBlock('用户意图判断', conditions.intent || ''),
    '示例问句：',
    examples || '- ',
  ].join('\n');
};

const parseStageBlock = (block: string): StageConfig => {
  const id = readScalar(block, '标识');
  const name = readScalar(block, '名称');
  if (!id || !name) {
    throw new Error('每个阶段都必须包含“标识”和“名称”。');
  }

  const maxTurnsValue = readScalar(block, '最大会话轮次');
  const maxTurns = maxTurnsValue && /^\d+$/.test(maxTurnsValue) && Number(maxTurnsValue) > 0 ? Number(maxTurnsValue) : '';
  const relation = readScalar(block, '条件关系');

  return {
    id,
    name,
    enabled: parseYesNo(readScalar(block, '开启'), true),
    focus: readTextBlock(block, '侧重点'),
    prompt: readTextBlock(block, '阶段提示词'),
    conditions: {
      max_turns: maxTurns,
      force_advance_on_max_turns: parseYesNo(readScalar(block, '超过最大轮次强制推进')),
      intent: readTextBlock(block, '用户意图判断'),
      keywords: parseKeywords(readScalar(block, '关键词')),
      relation: relation === '且' || relation === 'and' ? 'and' : 'or',
    },
    example_questions: readList(block, '示例问句'),
    children: [],
  };
};

const uniqueStageIds = (stages: StageConfig[]): void => {
  const ids = new Set<string>();
  for (const stage of stages) {
    for (const item of [stage, ...(stage.children || [])]) {
      if (ids.has(item.id)) {
        throw new Error(`阶段标识重复：${item.id}`);
      }
      ids.add(item.id);
    }
  }
};

export const formatPromptText = (text: string, name: string, side: ConfigSide): string =>
  [
    '# DeepFlow System Prompt',
    '格式版本：1',
    `模型侧：${side === 'left' ? '左侧' : '右侧'}`,
    `版本名称：${name}`,
    `导出时间：${new Date().toLocaleString('zh-CN', { hour12: false })}`,
    '',
    '说明：可直接编辑“System Prompt 内容”标题后的文字；导入后请确认并保存为新版本。',
    '',
    '## System Prompt 内容',
    text.trim(),
    '',
  ].join('\n');

export const parsePromptText = (raw: string): ImportedPromptText => {
  const content = normalizeNewlines(raw).trim();
  if (!content) throw new Error('导入文件为空。');

  const marker = /(?:^|\n)##\s*(?:System Prompt 内容|Prompt Content)\s*\n/i;
  const matched = marker.exec(content);
  const text = (matched ? content.slice((matched.index || 0) + matched[0].length) : content).trim();
  if (!text) throw new Error('未找到可导入的 System Prompt 内容。');

  return { text, name: readScalar(content, '版本名称') };
};

export const formatStageControlText = (stageControl: StageControlConfig, name: string, side: ConfigSide): string => {
  const chunks = [
    '# DeepFlow 会话阶段控制',
    '格式版本：1',
    `模型侧：${side === 'left' ? '左侧' : '右侧'}`,
    `版本名称：${name}`,
    `导出时间：${new Date().toLocaleString('zh-CN', { hour12: false })}`,
    `控制程度：${CONTROL_LEVEL_LABELS[stageControl.control_level] || '普通'}`,
    '',
    '说明：请保留“## 阶段 N”和“## 阶段 N / 子阶段 M”标题。长文本请保留 <<< 与 >>> 边界。导入后请确认并保存为新版本。',
  ];

  stageControl.stages.forEach((stage, index) => {
    chunks.push('', stageBlock(stage, `## 阶段 ${index + 1}`));
    (stage.children || []).forEach((child, childIndex) => {
      chunks.push('', stageBlock(child, `## 阶段 ${index + 1} / 子阶段 ${childIndex + 1}`));
    });
  });

  return `${chunks.join('\n')}\n`;
};

export const parseStageControlText = (raw: string): ImportedStageText => {
  const content = normalizeNewlines(raw).trim();
  if (!content) throw new Error('导入文件为空。');

  const headerPattern = /^##\s*阶段\s+(\d+)(?:\s*\/\s*子阶段\s+(\d+))?\s*$/gm;
  const matches = [...content.matchAll(headerPattern)];
  if (!matches.length) {
    throw new Error('未找到阶段内容。请使用系统导出的文本格式。');
  }

  const stages = new Map<number, StageConfig>();
  for (let index = 0; index < matches.length; index += 1) {
    const match = matches[index];
    const start = (match.index || 0) + match[0].length;
    const end = index + 1 < matches.length ? matches[index + 1].index || content.length : content.length;
    const stage = parseStageBlock(content.slice(start, end).trim());
    const parentIndex = Number(match[1]);
    const childIndex = match[2] ? Number(match[2]) : undefined;

    if (childIndex) {
      const parent = stages.get(parentIndex);
      if (!parent) {
        throw new Error(`子阶段 ${parentIndex}.${childIndex} 前缺少对应的父阶段。`);
      }
      parent.children = [...(parent.children || []), stage];
    } else {
      stages.set(parentIndex, stage);
    }
  }

  const orderedStages = [...stages.entries()]
    .sort(([left], [right]) => left - right)
    .map(([, stage]) => stage);
  uniqueStageIds(orderedStages);

  const levelValue = readScalar(content, '控制程度') || '普通';
  const controlLevel = CONTROL_LEVEL_BY_LABEL[levelValue] || 'normal';

  return {
    stageControl: { control_level: controlLevel, stages: orderedStages },
    name: readScalar(content, '版本名称'),
  };
};

export const downloadTextFile = (filename: string, content: string): void => {
  const blob = new Blob([`\uFEFF${content}`], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};

export const safeFilenamePart = (value: string): string =>
  value
    .trim()
    .replace(/[\\/:*?"<>|]+/g, '-')
    .replace(/\s+/g, '-')
    .slice(0, 48) || 'config';
